import React, { useContext } from 'react'
import { Button, Grid, GridCell, Text, View, useWindowResizeSensor } from 'oio-react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import Carousel from 'src/components/Carousel'
import Footer from 'src/components/Footer'
import GenericInitiativeListItem from 'src/components/Initiative/GenericListItemTile'
import PostListItemRow from 'src/components/Post/ListItemRow'
import RichText from 'src/components/utils/RichText'
import ThemeContext, { Theme } from 'src/libraries/contexts/Theme'
import { useInitiativeList, useMe, useOrganization } from 'src/graphql/hooks'

// =======================================================
// Layout
// =======================================================

const HomeContainerLayout2 = ({ organizationOverride }) => {
   const theme = useContext(ThemeContext)
   const { currentBreakpoint } = useWindowResizeSensor()
   const { organization: savedOrganization } = useOrganization()
   const { isLoggedIn, loading: userIsLoading } = useMe()
   const { initiativeList } = useInitiativeList({
      archived: 'exclude',
      limit: 30
   }, { notifyOnNetworkStatusChange: false })

   const postInitiatives = initiativeList.items.filter(i => i.class === 'post')
   const otherInitiatives = initiativeList.items.filter(i => i.class !== 'post')

   const organization = organizationOverride
      ? { ...savedOrganization, ...organizationOverride }
      : savedOrganization

   return (
      <>
         <Theme globalNavBorderColor="transparent" />
         <View
            width="100%"
            paddingTop={theme.viewportMarginTop}
            paddingLeft={theme.viewportMarginLeft}
            paddingRight={theme.viewportMarginRight}>
            <View
               display="flex"
               justifyContent="center"
               width="100%">
               <View
                  width="100%"
                  maxWidth={theme.viewportContentMaxWidth}
                  paddingLeft={currentBreakpoint > 'b' && theme.viewportPaddingLeft}
                  paddingRight={currentBreakpoint > 'b' && theme.viewportPaddingRight}
                  paddingBottom="30px">
                  <Grid spacing="0px">
                     <GridCell colspan="12[a-c] 6[d-f]" backgroundColor="#212229">
                        <View
                           float="left"
                           width="100%"
                           style={{ overflow: 'hidden' }}>
                           <View
                              float="left"
                              width="100%"
                              paddingTop="60%"
                              backgroundColor="#000"
                              backgroundSize="cover"
                              backgroundImage={organization.coverMedia && `url(${organization.coverMedia.file.thumbnailUrlW1600})`}
                              backgroundPosition="centre center"
                              style={{ overflow: 'hidden' }}
                           />
                           <View
                              float="left"
                              width="100%"
                              paddingVertical="24px[a] 30px[b] 48px[c] 60px[d-f]"
                              paddingHorizontal="24px[a] 30px[b] 48px[c] 72px[d-f]">
                              <View float="left" width="100%[a-c] 80%[d-f]">
                                 <Text
                                    letterSpacing="-0.25px"
                                    size="7[a] 8[b] 9[c] 8[d] 9[e-f]"
                                    weight="medium"
                                    color="#fff">
                                    {organization.name}
                                 </Text>
                              </View>
                              <View float="left" width="100%" height="18px[a-c] 24px[d] 24px[e-f]" />
                              <View
                                 float="left"
                                 width="42px[a-b] 48px[c-f]"
                                 height="6px[a-c] 6px[d-f]"
                                 backgroundColor="#fff"
                              />
                              <View float="left" width="100%" height="18px[a-c] 24px[d] 24px[e-f]" />
                              <View float="left" width="100%[a-c] 90%[d-f]">
                                 <Text
                                    lineHeight="140%[a-b] 140%[c] 140%[d] 150%[e-f]"
                                    size="4"
                                    color="#aaa">
                                    {organization.tagline}
                                 </Text>
                              </View>
                              <View
                                 float="left"
                                 width="100%"
                                 height="36px"
                                 borderBottom="1px solid rgba(255,255,255,0.2)"
                                 marginBottom="24px"
                              />
                              <View
                                 float="left[a-b] right[c-f]"
                                 width="100%[a-b]">
                                 {!userIsLoading && !isLoggedIn && (
                                    <Link to="/login">
                                       <Button
                                          size="md[a-c] sm[d] md[e-f]"
                                          width="100%[a-b] auto[c-f]"
                                          color="#fff"
                                          name="Sign In"
                                          outline
                                       />
                                    </Link>
                                 )}
                                 <View float="left" width="3px" height="6px" />
                                 <Link to="/explore">
                                    <Button
                                       size="md[a-c] sm[d] md[e-f]"
                                       width="100%[a-b] auto[c-f]"
                                       color="rgba(255,255,255,0.2)"
                                       name="Explore"
                                    />
                                 </Link>
                              </View>
                           </View>
                        </View>
                     </GridCell>
                     <GridCell colspan="12[a-c] 6[d-f]" backgroundColor="#f5f5f5">
                        <View
                           float="left"
                           width="100%"
                           minHeight="100vh"
                           paddingVertical="24px[a] 30px[b] 48px[c] 60px[d-f]"
                           paddingHorizontal="24px[a] 30px[b] 48px[c] 72px[d-f]">
                           <Grid columns="1" spacing="48px">
                              {organization.description && (
                                 <GridCell>
                                    <Text size="2.5" lineHeight="150%" color="#666">
                                       <RichText html={organization.description} />
                                    </Text>
                                 </GridCell>
                              )}
                              {postInitiatives.length > 0 && (
                                 <GridCell colspan="1">
                                    <View
                                       borderBottom="1px solid #ddd"
                                       paddingBottom="12px">
                                       <Text size="3" weight="medium">
                                          Latest News & Updates
                                       </Text>
                                    </View>
                                    <View
                                       float="left"
                                       width="100%">
                                       {postInitiatives.slice(0, 5).map(initiative => (
                                          <PostListItemRow key={initiative.id} {...initiative} />
                                       ))}
                                    </View>
                                 </GridCell>
                              )}
                              <GridCell colspan="1">
                                 <View
                                    borderBottom="1px solid #ddd"
                                    paddingBottom="12px"
                                    marginBottom="24px">
                                    <Text size="3" weight="medium">
                                       Latest Activity
                                    </Text>
                                 </View>
                                 <View
                                    float="left"
                                    width="100%"
                                    paddingHorizontal="6px[d-f]">
                                    <Carousel
                                       spacing={currentBreakpoint > 'b'
                                          ? 24
                                          : 18
                                       }
                                       numSlidesPerPane={{
                                          a: 2,
                                          b: 2,
                                          c: 4,
                                          d: 2,
                                          e: 3,
                                          f: 3
                                       }}>
                                       {otherInitiatives.map((initiative, index) => {
                                          const primaryRole = initiative.roles.find(r => r.type === 'primary')

                                          return (
                                             <View
                                                key={initiative.id}
                                                float="left"
                                                width="100%">
                                                <GenericInitiativeListItem
                                                   archived={initiative.archived}
                                                   initiativeClass={initiative.class}
                                                   coverURL={
                                                      initiative.coverMedia?.file.thumbnailUrlW480
                                                   }
                                                   date={initiative.startDateTimeISO8601}
                                                   link={`/initiatives/${initiative.slug}`}
                                                   name={initiative.name}
                                                   numActiveUsers={primaryRole?.numActiveUsers}
                                                   primaryRoleName={primaryRole?.name}
                                                   typeName={initiative.type.nameSingular}
                                                />
                                             </View>
                                          )
                                       })}
                                    </Carousel>
                                 </View>
                              </GridCell>
                           </Grid>
                        </View>
                     </GridCell>
                  </Grid>
               </View>
            </View>
            <Footer backgroundColor="#222" />
         </View>
      </>
   )
}

HomeContainerLayout2.propTypes = {
   organizationOverride: PropTypes.object
}

HomeContainerLayout2.defaultProps = {
   organizationOverride: undefined
}

export default HomeContainerLayout2
